<template>
  <div class="container" v-on="$listeners">
    <slot></slot>
    <div
      class="bubble"
      v-if="showBadge"
      :class="{
        dot: isDot,
        'anchor-top': top,
        'anchor-top-right': top && right,
        'anchor-top-left': top && left,
        'anchor-bottom': bottom,
        'anchor-bottom-right': bottom && right,
        'anchor-bottom-left': bottom && left,
        'help-widget': isHelpWidget,
        inside: inside
      }"
    >
      {{ countValue }}
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop } from 'vue-property-decorator'

@Component
export default class NNotificationBubble extends Vue {
  @Prop({ default: 0 }) count!: number
  @Prop({ default: false }) invisible!: boolean
  @Prop({ default: false }) showZero!: boolean
  @Prop({ default: false }) isHelpWidget!: boolean
  @Prop({ default: false, type: Boolean }) inside!: boolean
  @Prop({ default: 99 }) max!: number
  @Prop({ default: 'bubble', validator: (value: string) => ['bubble', 'dot'].includes(value) }) variant!:
    | 'bubble'
    | 'dot'
  @Prop({ default: () => ({ vertical: 'top', horizontal: 'right' }) }) anchorOrigin!: {
    horizontal: 'left' | 'right'
    vertical: 'bottom' | 'top'
  }

  get showBadge() {
    return (this.count === 0 && this.showZero && this.isBubble) || this.count > 0 || (this.isDot && !this.invisible)
  }

  private get top(): boolean {
    return this.anchorOrigin.vertical === 'top'
  }

  private get right(): boolean {
    return this.anchorOrigin.horizontal === 'right'
  }

  private get bottom(): boolean {
    return this.anchorOrigin.vertical === 'bottom'
  }

  private get left(): boolean {
    return this.anchorOrigin.horizontal === 'left'
  }

  private get countValue(): string {
    let count = `${Math.min(this.count, this.max)}`

    return this.isBubble ? count : ''
  }

  private get isDot(): boolean {
    return this.variant === 'dot'
  }

  private get isBubble(): boolean {
    return this.variant === 'bubble'
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.container {
  position: relative;
  display: inline-flex;
}

.anchor-top {
  top: 0;
  &-right {
    right: 0;
    transform: scale(1) translate(50%, -50%);
    transform-origin: 100% 0%;

    &.help-widget {
      transform: scale(1) translate(-50%, 0);
    }
  }

  &-left {
    left: 0;
    transform: scale(1) translate(-50%, -50%);
    transform-origin: 0% 0%;
  }

  &.inside {
    transform: scale(1) translate(0%, 0%);
  }
}

.anchor-bottom {
  bottom: 0;
  &-right {
    right: 0;
    transform: scale(1) translate(50%, 50%);
    transform-origin: 100% 100%;
  }
  &-left {
    left: 0;
    transform: scale(1) translate(-50%, 50%);
    transform-origin: 0% 100%;
  }

  &.inside {
    transform: scale(1) translate(0%, 100%);
  }
}

.bubble {
  display: flex;
  background-color: $notch-color-semantic-alert-500;
  border-radius: $notch-radius-full;
  color: $notch-color-neutral-grey-000;
  width: $notch-space-4x;
  height: $notch-space-4x;
  padding: 0 $notch-space-1x;
  z-index: $notch-layer-1;
  position: absolute;
  flex-wrap: wrap;
  box-sizing: border-box;
  transition: transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  align-content: center;
  flex-direction: row;
  justify-content: center;
  @include notch-text-label-strong-XXS;
}
.dot {
  height: $notch-space-2x;
  padding: 0;
  width: $notch-space-2x;
  border-radius: $notch-space-1x;
}
</style>
